import React, { useState, useMemo } from 'react';
import OppsSeasonalityTableView from '../components/OppsSeasonalityTableView';
import SelectDropdown from '../../../components/SelectDropdown';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DownloadButton from '../../../components/DownloadReport';
import { useSelector } from 'react-redux';
import selectors from '../../../modules/selectors/datePickerSelectors';
import { useSpendAndOppsHook } from '../hooks/useSpendAndOppsHook';
import { OPPS_DATA, SPENDS_DATA } from '../constants';
import OverTimeLineGraph from './OverTimeLineGraph';
import PercOverTimeLineGraph from './PercOverTimeLineGraph';
import { ToggleComponent } from '../../../components/Toggle';
import {
  ENABLE_LOCAL_SPEND,
  getSettingValue,
  isStoreValid,
  LOCAL_BRANCH_SPEND,
} from '../../../constants/utils';
import { getSpendAndOppsFileName } from '../service/SpendAndOppurtunitiesService';

// Memoized graph components to prevent unnecessary re-renders
const MemoizedOverTimeLineGraph = React.memo(OverTimeLineGraph);
const MemoizedPercOverTimeLineGraph = React.memo(PercOverTimeLineGraph);

const SpendAndOpportunities = () => {
  const { userStores, userData } = useSelector((state) => state.auth);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const options = useMemo(() => {
    const stores = userStores.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter((store) => store.value === 1);
    if (GaleMarkets?.length) {
      return [
        GaleMarkets[0],
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    } else {
      return [
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
  }, [userStores]);
  const [selectedStore, setSelectedStore] = useState(options[0].value);
  const [viewType, setViewType] = useState('Weekly');
  const { transformedDataWithDate } = useSelector(selectors?.datePickerCombinedData);
  const currentYear = userData?.data?.current_year;
  const {
    oppsData,
    spendsData,
    oppsheaders,
    spendsheaders,
    isLoading,
    error,
    downloadReportData,
    spendToggleState,
    setSpendToggleState,
    showSpendToggle,
    allStores,
  } = useSpendAndOppsHook(selectedStore, viewType, currentYear, userStores, isDownloading);
  const storeArray = userData?.data?.stores_data?.map((store) => store.branch_number);
  const storeList = storeArray;

  const handleStoreChange = (value) => {
    setSelectedStore(value);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setViewType(newView);
    }
  };

  // Memoize the graph visibility conditions
  const showGraphs = useMemo(
    () => ({
      base: Boolean(currentYear && transformedDataWithDate?.length),
      percentage: Boolean(currentYear && transformedDataWithDate?.length && !spendToggleState),
    }),
    [currentYear, transformedDataWithDate?.length, spendToggleState]
  );

  // Memoize graph props to prevent recreation on every render
  const oppsGraphProps = useMemo(
    () => ({
      selectedStore,
      currentYear,
      totalYears: transformedDataWithDate?.length,
      userStores,
      dataType: OPPS_DATA,
      spendToggleState,
    }),
    [selectedStore, currentYear, transformedDataWithDate?.length, userStores, spendToggleState]
  );

  const spendsGraphProps = useMemo(
    () => ({
      selectedStore,
      currentYear,
      totalYears: transformedDataWithDate?.length,
      userStores,
      dataType: SPENDS_DATA,
      spendToggleState,
    }),
    [selectedStore, currentYear, transformedDataWithDate?.length, userStores, spendToggleState]
  );

  // Memoize the table props as well for further optimization
  const oppsTableProps = useMemo(
    () => ({
      viewType,
      data: oppsData,
      headers: oppsheaders,
      isLoading,
      error,
      type: OPPS_DATA,
    }),
    [viewType, oppsData, oppsheaders, isLoading, error]
  );

  const spendsTableProps = useMemo(
    () => ({
      viewType,
      data: spendsData,
      headers: spendsheaders,
      isLoading,
      error,
      type: SPENDS_DATA,
    }),
    [viewType, spendsData, spendsheaders, isLoading, error]
  );

  return (
    <div
      style={{
        height: '88vh',
        overflow: 'scroll',
        padding: '20px',
        width: 'inherit',
        paddingBottom: '100px',
      }}
    >
      <div style={{ width: 'fitContent', marginBottom: '55px' }}>
        <div
          style={{
            width: '90%',
            textAlign: 'left',
            margin: 'auto',
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Spend and Opportunities</span>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            {showSpendToggle && getSettingValue(userData?.data?.settings, ENABLE_LOCAL_SPEND) && (
              <ToggleComponent
                setState={setSpendToggleState}
                currentState={spendToggleState}
                disabled={isLoading}
                toggleText={LOCAL_BRANCH_SPEND}
                hideToggle={isStoreValid(selectedStore, allStores)}
              />
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '13px',
          width: '90%',
          margin: 'auto',
        }}
      >
        <div style={{ marginRight: 'auto' }}>
          <SelectDropdown
            options={options}
            label={'Select Branch'}
            handleFilterChange={handleStoreChange}
            singleSelectDefaultValue={selectedStore}
            disabled={isLoading}
          />
        </div>
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          aaria-label="Toggle Spends And Opps Table View"
          sx={{ paddingTop: '7px', paddingRight: '20px' }}
          disabled={isLoading}
        >
          <ToggleButton
            value={'Weekly'}
            style={{
              background: viewType === 'Weekly' ? 'black' : isLoading ? '#e4e4e7' : '#fafafa',
              color: viewType === 'Weekly' ? 'white' : isLoading ? '#a9a9ab' : 'black',
              borderColor: isLoading ? '#a9a9ab' : 'black',
              textTransform: 'capitalize',
              height: '40px',
            }}
          >
            Weekly
          </ToggleButton>
          <ToggleButton
            value="Monthly"
            style={{
              background: viewType === 'Monthly' ? 'black' : isLoading ? '#e4e4e7' : '#fafafa',
              color: viewType === 'Monthly' ? 'white' : isLoading ? '#a9a9ab' : 'black',
              borderColor: isLoading ? '#a9a9ab' : 'black',
              textTransform: 'capitalize',
              height: '40px',
            }}
          >
            Monthly
          </ToggleButton>
        </ToggleButtonGroup>
        <DownloadButton
          data={downloadReportData}
          downloadCheck={downloadReportData?.length === storeList?.length && isDownloading}
          filename={getSpendAndOppsFileName({ spendToggleState, viewType })}
          setIsDownload={setIsDownload}
          tab={'spend_and_opportunities'}
          setIsDownloading={setIsDownloading}
          isDownloading={isDownloading}
          isDisabled={isLoading || downloadReportData?.length === 0}
        />
      </div>

      {/* Opportunities section with optimized rendering */}
      <OppsSeasonalityTableView {...oppsTableProps} />

      <div style={{ width: '90%', margin: 'auto' }}>
        {showGraphs.base && <MemoizedOverTimeLineGraph key="opps-over-time" {...oppsGraphProps} />}

        {showGraphs.percentage && (
          <MemoizedPercOverTimeLineGraph key="opps-perc-over-time" {...oppsGraphProps} />
        )}
      </div>
      {/* Spends section with optimized rendering */}
      <OppsSeasonalityTableView {...spendsTableProps} />
      <div style={{ width: '90%', margin: 'auto' }}>
        {showGraphs.base && (
          <MemoizedOverTimeLineGraph key="spends-over-time" {...spendsGraphProps} />
        )}

        {showGraphs.percentage && (
          <MemoizedPercOverTimeLineGraph key="spends-perc-over-time" {...spendsGraphProps} />
        )}
      </div>
    </div>
  );
};

export default React.memo(SpendAndOpportunities);
